/**
 * Which page the app shows inside the workspace (decision 77; part 11d log, table R): the home page, the writer, the
 * settings, (part 38d) the Sharing page or (part 38c) Shared with me and a shared book's read view. The page is kept in the address's fragment, so a reload stays on it and the browser's Back and Forward move
 * between pages; a launch (no fragment) opens the home page. Pure: the fragment is read at the boundary, here.
 */

/** A page of the app (R1). */
export type Route = 'home' | 'write' | 'settings' | 'sharing' | 'shared' | 'reading';

/** Each page's fragment, as the address shows it. */
const FRAGMENTS: Readonly<Record<Route, string>> = Object.freeze({ home: '#/', write: '#/write', settings: '#/settings', sharing: '#/sharing', shared: '#/shared', reading: '#/read/' });

/** Part 38c: the read view's prefix, then the invite's ID (16 bytes, base64url: 22 characters). */
const READ_PREFIX = '#/read/';
const INVITE_ID = /^[A-Za-z0-9_-]{22}$/u;

/**
 * Part 38c: the invite a read view's fragment names, or null for any other fragment (one that starts as one but names
 * no ID of the shape is Shared with me). Untrusted, as every fragment: whether the claim is held is the cache's to say.
 */
export function readingOf(fragment: string): string | null {
  if (!fragment.startsWith(READ_PREFIX)) return null;
  const invite = fragment.slice(READ_PREFIX.length);
  return INVITE_ID.test(invite) ? invite : null;
}

/** Part 38c: the read view's fragment for `invite`. */
export function readingFragment(invite: string): string {
  return `${READ_PREFIX}${invite}`;
}

/**
 * R1, R2: the page an address's fragment names. The fragment is untrusted (anyone can write a link): only the exact
 * fragments above name a page, and everything else, an empty one included, is the home page.
 */
export function routeOf(fragment: string): Route {
  if (fragment === FRAGMENTS.write) return 'write';
  if (fragment === FRAGMENTS.settings) return 'settings';
  if (fragment === FRAGMENTS.sharing) return 'sharing';
  if (fragment === FRAGMENTS.shared) return 'shared';
  if (fragment.startsWith(READ_PREFIX)) return readingOf(fragment) === null ? 'shared' : 'reading';
  return 'home';
}

/** The fragment that names `route` (R3). */
export function fragmentOf(route: Route): string {
  return FRAGMENTS[route];
}

/** Part 36a, SU6, C5: a verify link's fragment: `#/verify/` and the link's token (32 bytes, base64url: 43 characters). */
const VERIFY_PREFIX = '#/verify/';
const TOKEN = /^[A-Za-z0-9_-]{43}$/u;

/**
 * Part 36a, SU6, SU8: what a fragment says of a verify link: its token; `bad` for a fragment that starts as one but
 * holds no token of the shape (said at once, nothing sent); null for any other fragment. The fragment is untrusted:
 * whether the token is a live link is the relay's to say (`invalid-link`).
 */
export function verifyLinkOf(fragment: string): { readonly token: string } | 'bad' | null {
  if (!fragment.startsWith(VERIFY_PREFIX)) return null;
  const token = fragment.slice(VERIFY_PREFIX.length);
  return TOKEN.test(token) ? { token } : 'bad';
}

/** Part 36c, table LK: the kinds of mailed link besides the verify link, by their fragment's prefix. */
export const ACCOUNT_LINKS = Object.freeze({ confirm: '#/confirm/', enrol: '#/enrol/', reset: '#/reset/', keep: '#/delete-cancel/' });

/** A kind of mailed link (LK): a change's, an enrolment's, a reset's, or a deletion's cancel. */
export type AccountLinkKind = keyof typeof ACCOUNT_LINKS;

const LINK_KINDS: readonly AccountLinkKind[] = ['confirm', 'enrol', 'reset', 'keep'];

/**
 * Part 36c, LK: what a fragment says of a mailed link: its kind and token; the kind with a null token for a fragment that
 * starts as one but holds no token of the shape (said at once, nothing sent); null for any other fragment. Untrusted, as
 * the verify link's: whether the token is live is the relay's to say.
 */
export function accountLinkOf(fragment: string): { readonly kind: AccountLinkKind; readonly token: string | null } | null {
  const kind = LINK_KINDS.find((each) => fragment.startsWith(ACCOUNT_LINKS[each]));
  if (kind === undefined) return null;
  const token = fragment.slice(ACCOUNT_LINKS[kind].length);
  return { kind, token: TOKEN.test(token) ? token : null };
}
